import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Avatar, Box, Button, Chip, CircularProgress, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkIcon from '@mui/icons-material/Link';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MediaViewer, { ViewerItem } from 'components/MediaViewer';
import useApi from 'hooks/useApi';
import { Announcement, AnnouncementAttachment } from 'types/auth';
import { formatReleaseDate } from './AnnouncementsList';
import AnnouncementTypeChip from './AnnouncementTypeChip';
import useAttachmentUrls from '../hooks/useAttachmentUrls';

interface AnnouncementDetailProps {
  announcement: Announcement;
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

// One view receipt, as the /announcement/uid/:uid/views endpoint returns it.
interface AnnouncementViewer {
  uid: string | null;
  name: string | null;
  email: string | null;
  role: string | null;
  viewedAt: string | null;
  lastViewedAt: string | null;
  viewCount: number | null;
}

// "3 Sep 2026, 4:07 PM" — viewer timestamps carry the time too, since two opens on the
// same day are common and the date alone cannot tell them apart. Returns '' for a
// missing or unparseable value so the caller can drop the line.
const formatViewedAt = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : format(date, 'd MMM yyyy, h:mm a');
};

// Up to two letters for the avatar, from the name if there is one, else the email.
const initialsFor = (viewer: AnnouncementViewer) => {
  const source = viewer.name || viewer.email || '';
  const parts = source.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Every attachment card is exactly this size, whatever it holds — a video thumbnail, a
// file icon or an image — so rows never come out ragged.
const CARD_HEIGHT = 108;
const CARD_MIN_WIDTH = 240;
const CARD_GAP = 12;

// "2.4 MB". Returns '' when the size was never recorded, so the caller can drop the
// separator rather than render a stray bullet.
const formatBytes = (bytes?: number | null) => {
  if (!bytes || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value >= 10 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`;
};

// "PDF" / "Image" / "Video" / "Link" — the trailing half of the card subtitle.
const kindLabel = (attachment: AnnouncementAttachment) => {
  if (attachment.media_type === 'image') return attachment.is_link ? 'Image link' : 'Image';
  if (attachment.media_type === 'video') return attachment.is_link ? 'Video link' : 'Video';
  // A link has no mime type to read, and its URL often has no extension either.
  if (attachment.is_link) return 'Link';
  const subtype = attachment.mime_type?.split('/')[1] ?? '';
  if (subtype === 'pdf') return 'PDF';
  const extension = attachment.file_name?.split('.').pop();
  return (extension || subtype || 'File').toUpperCase();
};

const AnnouncementDetail = ({ announcement, canManage, onEdit, onDelete }: AnnouncementDetailProps) => {
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const { getAnnouncementViews } = useApi();

  // "Viewed by" — Super Admin only, so gated on canManage (which is isSuperAdmin). The
  // endpoint rejects everyone else, so there is nothing to fetch for them.
  const [viewers, setViewers] = useState<AnnouncementViewer[]>([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [viewersError, setViewersError] = useState(false);
  const [viewerSearch, setViewerSearch] = useState('');

  useEffect(() => {
    // Reset the filter whenever the open announcement changes, so a query from one
    // announcement's viewers doesn't carry over to the next.
    setViewerSearch('');
    if (!canManage || !announcement.uid) {
      setViewers([]);
      return;
    }
    let cancelled = false;
    setViewersLoading(true);
    setViewersError(false);
    getAnnouncementViews(announcement.uid)
      .then((res: any) => {
        if (!cancelled) setViewers(res?.data?.viewers ?? []);
      })
      .catch(() => {
        if (!cancelled) setViewersError(true);
      })
      .finally(() => {
        if (!cancelled) setViewersLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // getAnnouncementViews is recreated each render (not memoized in ApiContext); depending
    // on it would refetch in a loop. The uid is the only input that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcement.uid, canManage]);

  const attachments = announcement.attachments ?? [];
  // Prefers the URL the API sent, and re-signs from `file_path` when it didn't send one.
  const srcFor = useAttachmentUrls(attachments);

  // Each non-empty line of the description becomes a bullet.
  const bullets = (announcement.description ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  // Images and videos open in the lightbox; their index there is not their index in
  // `attachments`, so resolve against the viewable subset.
  const viewable = attachments.filter(
    (attachment) => (attachment.media_type === 'image' || attachment.media_type === 'video') && Boolean(srcFor(attachment))
  );
  const viewerItems: ViewerItem[] = viewable.map((attachment) => ({
    src: srcFor(attachment),
    caption: attachment.file_name,
    type: attachment.media_type === 'video' ? 'video' : 'image'
  }));

  const openViewer = (attachment: AnnouncementAttachment) => {
    const position = viewable.indexOf(attachment);
    if (position > -1) setViewerIndex(position);
  };

  const sectionLabel = (text: string) => (
    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 500, mb: 1 }}>
      {text}
    </Typography>
  );

  // Filter the "Viewed by" list by name, email or role — case-insensitive substring.
  const viewerQuery = viewerSearch.trim().toLowerCase();
  const filteredViewers = viewerQuery
    ? viewers.filter((viewer) =>
        [viewer.name, viewer.email, viewer.role].some((field) => (field ?? '').toLowerCase().includes(viewerQuery))
      )
    : viewers;

  const renderCard = (attachment: AnnouncementAttachment, index: number) => {
    const src = srcFor(attachment);
    const isVideo = attachment.media_type === 'video';
    const isImage = attachment.media_type === 'image';
    const subtitle = [formatBytes(attachment.file_size), kindLabel(attachment)].filter(Boolean).join(' • ');

    // Video: full-bleed thumbnail with the name laid over the bottom edge.
    if (isVideo && src) {
      return (
        <Box
          key={`${attachment.file_path}-${index}`}
          onClick={() => openViewer(attachment)}
          title={attachment.file_name}
          sx={{
            position: 'relative',
            height: CARD_HEIGHT,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: '#0b0b0b',
            cursor: 'pointer',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            // The card is the button now, so the hover has to carry the affordance on
            // its own — there is no icon left to hint at it.
            '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
            '&:hover .play-badge': { transform: 'scale(1.12)', bgcolor: '#fff' }
          }}
        >
          <Box
            component="video"
            src={src}
            preload="metadata"
            muted
            sx={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0.75) 100%)'
            }}
          >
            <Box
              className="play-badge"
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.15s ease, background-color 0.15s ease'
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 22, color: '#111' }} />
            </Box>
          </Box>
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              left: 10,
              right: 10,
              bottom: 8,
              color: '#fff',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {attachment.file_name}
          </Typography>
        </Box>
      );
    }

    // Everything else: icon or thumbnail, name, size • kind, and a trailing action.
    return (
      <Stack
        key={`${attachment.file_path}-${index}`}
        direction="row"
        alignItems="center"
        spacing={1.5}
        title={attachment.file_name}
        sx={{
          height: CARD_HEIGHT,
          px: 1.5,
          borderRadius: 2,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
          cursor: src ? 'pointer' : 'default',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
          // Whole card is the click target — an image opens the viewer, anything else
          // opens in a new tab. The hover is the only cue, so it has to be legible.
          ...(src && {
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 2,
              borderColor: 'primary.main',
              bgcolor: 'background.paper'
            }
          })
        }}
        onClick={
          src
            ? () => {
                if (isImage) openViewer(attachment);
                else window.open(src, '_blank', 'noopener,noreferrer');
              }
            : undefined
        }
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            flexShrink: 0,
            borderRadius: 1.5,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isImage ? 'grey.200' : attachment.is_link ? '#E3F2FD' : '#E8F5E9'
          }}
        >
          {isImage && src ? (
            <Box component="img" src={src} alt={attachment.file_name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : attachment.is_link ? (
            <LinkIcon sx={{ fontSize: 26, color: '#1565C0' }} />
          ) : (
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 26, color: '#2E7D32' }} />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {attachment.file_name || 'Attachment'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {src ? subtitle : 'Preview unavailable'}
          </Typography>
        </Box>
      </Stack>
    );
  };

  return (
    <Box sx={{ p: 3.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
            {announcement.title}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
            <AnnouncementTypeChip type={announcement.type} />
            <Typography variant="caption" color="text.secondary">
              Released {formatReleaseDate(announcement.createdAt)}
            </Typography>
          </Stack>
        </Box>
        {canManage && (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon fontSize="small" />} onClick={onEdit}>
              Edit
            </Button>
            <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlineIcon fontSize="small" />} onClick={onDelete}>
              Delete
            </Button>
          </Stack>
        )}
      </Stack>

      {canManage && (
        <Box sx={{ mt: 3 }}>
          {sectionLabel('Target Clients')}
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {announcement.allClients ? (
              <Chip size="small" label="All clients" sx={{ borderRadius: 999, bgcolor: 'grey.100', fontWeight: 500 }} />
            ) : (
              (announcement.clients ?? [])
                .filter((client) => client?.uid)
                .map((client) => (
                  <Chip
                    key={client.uid}
                    size="small"
                    label={client.name}
                    sx={{ borderRadius: 999, bgcolor: 'grey.100', fontWeight: 500 }}
                  />
                ))
            )}
          </Stack>
        </Box>
      )}

      {/* Only rendered when the announcement is actually process-targeted. Showing an
          empty "Process —" row on every client-wide announcement would imply the funnel
          was used and left blank, rather than not used at all. Roles ride in the same
          block because they are never set without a process. */}
      {canManage && announcement.process?.uid && (
        <Box sx={{ mt: 3 }}>
          {sectionLabel('Process')}
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            <Chip size="small" label={announcement.process.name} sx={{ borderRadius: 999, bgcolor: 'grey.100', fontWeight: 500 }} />
          </Stack>

          <Box sx={{ mt: 2 }}>
            {sectionLabel('Roles')}
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {(announcement.roles ?? [])
                .filter((role) => role?.uid)
                .map((role) => (
                  <Chip key={role.uid} size="small" label={role.name} sx={{ borderRadius: 999, bgcolor: 'grey.100', fontWeight: 500 }} />
                ))}
            </Stack>
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        {sectionLabel('Description')}
        <Box component="ul" sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
          {bullets.map((line, index) => (
            <Box component="li" key={index} sx={{ mb: 0.75, '&::marker': { color: 'text.secondary' } }}>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                {line}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {attachments.length > 0 && (
        <Box sx={{ mt: 3 }}>
          {sectionLabel('Attachments')}
          {/* auto-fill rather than MUI breakpoints on purpose: breakpoints read the
              viewport, but this pane's width is the viewport minus the nav and the 320px
              list, so a "large screen" can still leave a narrow column here. auto-fill
              measures the actual space and fits as many cards per row as it holds,
              stepping down to one on a narrow pane, with no column cap. */}
          <Box
            sx={{
              display: 'grid',
              gap: `${CARD_GAP}px`,
              gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}px, 1fr))`
            }}
          >
            {attachments.map(renderCard)}
          </Box>
        </Box>
      )}

      {canManage && (
        <Box sx={{ mt: 3 }}>
          {sectionLabel(
            viewers.length === 0
              ? 'Viewed by'
              : viewerQuery
              ? `Viewed by (${filteredViewers.length} of ${viewers.length})`
              : `Viewed by (${viewers.length})`
          )}
          {viewersLoading ? (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading…</Typography>
            </Stack>
          ) : viewersError ? (
            <Typography variant="body2" color="text.secondary">
              Couldn&apos;t load viewers.
            </Typography>
          ) : viewers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No one has viewed this yet.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search by name, email or role"
                value={viewerSearch}
                onChange={(event) => setViewerSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="disabled" />
                    </InputAdornment>
                  )
                }}
              />
              {filteredViewers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No viewers match &ldquo;{viewerSearch.trim()}&rdquo;.
                </Typography>
              ) : (
                filteredViewers.map((viewer, index) => (
                  <Box key={viewer.uid ?? viewer.email ?? index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 13 }}>{initialsFor(viewer)}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {viewer.name || viewer.email || 'Unknown user'}
                        </Typography>
                        {viewer.role && (
                          <Chip
                            label={viewer.role}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: 11, flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
                          />
                        )}
                      </Box>
                      {viewer.name && viewer.email && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {viewer.email}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      {viewer.viewedAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {formatViewedAt(viewer.viewedAt)}
                        </Typography>
                      )}
                      {viewer.viewCount != null && viewer.viewCount > 1 && (
                        <Typography variant="caption" color="text.secondary">
                          opened {viewer.viewCount}×
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </Stack>
          )}
        </Box>
      )}

      <MediaViewer
        open={viewerIndex !== null}
        items={viewerItems}
        index={viewerIndex ?? 0}
        onIndexChange={setViewerIndex}
        onClose={() => setViewerIndex(null)}
      />
    </Box>
  );
};

export default AnnouncementDetail;
